/**
 * POSIX ACL grammar of ADLS Gen2 — S8 Task 8.2, process-agnostic. The service spelling (NOTES «S8: live-probed facts»
 * A8) is `[default:]type:[id]:rwx`, comma-separated, in the order owner, named users, owning group, named groups, mask,
 * other — then the same with `default:`. The permission string (A7) is nine `rwx` characters, the last one `t` / `T` when
 * the sticky bit is set (with / without other-execute), plus `+` when extended entries exist. Nothing here throws.
 */
import type { StorageAclEntry, StorageAclEntryType, StorageAclPermissions } from '../../types/azureStorage';

export type AclParse = { ok: true; entries: StorageAclEntry[] } | { ok: false; reason: string };

const TYPES: readonly StorageAclEntryType[] = ['user', 'group', 'mask', 'other'];
const PERMS = /^[r-][w-][x-]$/u;

export const permissionsOf = (text: string): StorageAclPermissions => ({ read: text[0] === 'r', write: text[1] === 'w', execute: text[2] === 'x' });
export const formatPermissions = (p: StorageAclPermissions): string => `${p.read ? 'r' : '-'}${p.write ? 'w' : '-'}${p.execute ? 'x' : '-'}`;

/** An entry's identity: scope, type and object id — two entries with the same key name one principal in one scope. */
export const aclEntryKey = (e: Pick<StorageAclEntry, 'scope' | 'type' | 'id'>): string => `${e.scope}|${e.type}|${e.id ?? ''}`;
const entryText = (e: StorageAclEntry): string => `${aclEntryKey(e)}|${formatPermissions(e.permissions)}`;
/** The same entries with the same bits, in any order (the service orders entries itself) — the ONE ACL comparison (review opus F10). */
export const sameAclEntries = (a: readonly StorageAclEntry[], b: readonly StorageAclEntry[]): boolean =>
  a.length === b.length && [...a.map(entryText)].sort().join('\n') === [...b.map(entryText)].sort().join('\n');

function parseEntry(raw: string): StorageAclEntry | string {
  const isDefault = raw.startsWith('default:');
  const body = isDefault ? raw.slice('default:'.length) : raw;
  const first = body.indexOf(':');
  const last = body.lastIndexOf(':');
  if (first < 0 || first === last) return `"${raw}" is not type:id:permissions.`;
  const type = body.slice(0, first) as StorageAclEntryType;
  const id = body.slice(first + 1, last);
  const perms = body.slice(last + 1);
  if (!TYPES.includes(type)) return `"${raw}" has an unknown entry type.`;
  if (!PERMS.test(perms)) return `"${raw}" has invalid permissions (expected three of r, w, x or -).`;
  if (id && (type === 'mask' || type === 'other')) return `"${raw}": ${type} entries have no id.`;
  return { scope: isDefault ? 'default' : 'access', type, ...(id ? { id } : {}), permissions: permissionsOf(perms) };
}

/** Parses an `x-ms-acl` string; `ok: false` with a reason for anything the service would not send. */
export function parseAclString(text: string): AclParse {
  if (text.trim() === '') return { ok: false, reason: 'The ACL is empty.' };
  const entries: StorageAclEntry[] = [];
  for (const raw of text.split(',')) {
    const entry = parseEntry(raw);
    if (typeof entry === 'string') return { ok: false, reason: entry };
    entries.push(entry);
  }
  return { ok: true, entries };
}

/** Formats entries in the order given (the service's own order when they came from `parseAclString`). */
export function formatAclString(entries: readonly StorageAclEntry[]): string {
  return entries.map((e) => `${e.scope === 'default' ? 'default:' : ''}${e.type}:${e.id ?? ''}:${formatPermissions(e.permissions)}`).join(',');
}

/** The SDK's `PathPermissions` object back into the service string (the listing facet). */
export function formatPermissionString(p: { owner: StorageAclPermissions; group: StorageAclPermissions; other: StorageAclPermissions; stickyBit?: boolean; extendedAcls?: boolean }): string {
  const other = formatPermissions(p.other);
  const last = p.stickyBit ? (p.other.execute ? 't' : 'T') : other[2];
  return `${formatPermissions(p.owner)}${formatPermissions(p.group)}${other.slice(0, 2)}${last}${p.extendedAcls ? '+' : ''}`;
}
